import React, { FC } from 'react';
import { createAvatar } from '@dicebear/core';
import { initials } from '@dicebear/collection';
import { useNavigate } from 'react-router-dom';
import { UserPlus, Check, ExternalLink, MessageCircle } from 'lucide-react';
import { PUBLIC_PROFILE_ROUTE } from '../../profile';
import { CHAT_PAGE_ROUTE, DM_PAGE_ROUTE } from '../../chat';
import useMediaQuery from '@mui/system/useMediaQuery/useMediaQuery';

export interface UserAvatarData {
  id: string;
  name: string;
  photoURL?: string;
  firebaseUID: string;
}

interface InvitePageUserAvatarProps {
  user: UserAvatarData;
  isSelected: boolean;
  isSelectMode: boolean;
  isInRoom: boolean;
  onSelect: (userId: string) => void;
  onClickInvite: () => void;
  /** This player's invite request is in flight. */
  isInviting?: boolean;
  /** Any invite request is in flight (disables every Invite button). */
  inviteBusy?: boolean;
  state?: 'online' | 'offline' | 'joined' | 'rejoined' | 'left';
}

const STATE_LABEL: Record<string, string> = {
  online: 'Online',
  joined: 'In game',
  offline: 'Offline',
  rejoined: 'Rejoined',
  left: 'Left',
};

const generateAvatarUrl = (name: string | undefined): string => {
  if (!name) return '';
  return createAvatar(initials, { seed: name }).toDataUri();
};

export const InvitePageUserAvatar: FC<InvitePageUserAvatarProps> = ({
  user,
  isSelected,
  isSelectMode,
  isInRoom,
  onSelect,
  onClickInvite,
  isInviting = false,
  inviteBusy = false,
  state = 'offline',
}) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery('(max-width: 640px)');

  const handleRowClick = () => {
    if (isSelectMode && isInRoom) {
      onSelect(user.id);
    }
  };

  const handleViewProfile = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate([PUBLIC_PROFILE_ROUTE.EXACT_PATH, '?user_id=', user.id].join(''));
  };

  const handleDirectMessage = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(
      [
        !isMobile ? DM_PAGE_ROUTE.EXACT_PATH : CHAT_PAGE_ROUTE.EXACT_PATH,
        '?receiverID=',
        user.firebaseUID,
      ].join('')
    );
  };

  const handleInvite = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClickInvite();
  };

  const dotState = STATE_LABEL[state] ? state : 'offline';
  const stateLabel = STATE_LABEL[state] ?? 'Offline';

  const selectable = isSelectMode && isInRoom;

  return (
    <div
      role={selectable ? 'button' : undefined}
      tabIndex={selectable ? 0 : undefined}
      aria-pressed={selectable ? isSelected : undefined}
      aria-label={
        selectable
          ? `${isSelected ? 'Deselect' : 'Select'} ${user.name}`
          : undefined
      }
      onClick={handleRowClick}
      onKeyDown={e => {
        if (!selectable) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleRowClick();
        }
      }}
      className={[
        'bh-player-row bh-player-row--social',
        isSelected ? 'bh-player-row--selected' : '',
        selectable ? 'cursor-pointer' : 'cursor-default',
      ].join(' ')}
    >
      {/* Selection checkbox */}
      {selectable && (
        <span className="bh-player-row__check" aria-hidden="true">
          {isSelected && <Check size={13} strokeWidth={3} />}
        </span>
      )}

      {/* Avatar with status dot */}
      <div className="relative flex-shrink-0">
        <img
          src={user.photoURL || generateAvatarUrl(user.name)}
          alt=""
          className="bh-player-row__avatar"
        />
        <span
          className="bh-player-row__dot"
          data-state={dotState}
          aria-hidden="true"
        />
      </div>

      {/* Name + status */}
      <div className="flex-1 min-w-0">
        <p className="bh-player-row__name" title={user.name}>
          {user.name}
        </p>
        <p className="bh-player-row__state" data-state={dotState}>
          {stateLabel}
        </p>
      </div>

      {/* Actions — hidden in select mode */}
      {!isSelectMode && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            type="button"
            onClick={handleDirectMessage}
            aria-label={`Message ${user.name}`}
            title={`Message ${user.name}`}
            className="bh-icon-btn"
          >
            <MessageCircle size={18} aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={handleViewProfile}
            aria-label={`View ${user.name}'s profile`}
            title="View profile"
            className="bh-icon-btn"
          >
            <ExternalLink size={18} aria-hidden="true" />
          </button>

          {isInRoom && (
            <button
              type="button"
              onClick={handleInvite}
              disabled={inviteBusy}
              aria-busy={isInviting}
              aria-label={
                isInviting ? `Sending invite to ${user.name}` : `Invite ${user.name}`
              }
              className="bh-btn bh-btn--primary bh-btn--small bh-player-row__invite"
            >
              <UserPlus size={16} aria-hidden="true" />
              <span className="hidden sm:inline">
                {isInviting ? 'Sending…' : 'Invite'}
              </span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};
